/**
 * @fileoverview Needs Review tab, including table, bulk actions, and review workflow.
 * @module features/sku-alias/pages/NeedsReviewTab
 */

import { ReactElement, useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useSearchParams } from 'react-router-dom'
import { toast } from 'sonner'
import { motion, AnimatePresence } from 'framer-motion'

import {
  useAssignAliasMutation,
  useNeedsReviewQuery,
  useGenerateSkuAiSuggestionsMutation,
  useClearSkuSuggestionMutation,
  type NeedsReviewSortKey,
} from '@/features/sku-alias/api/needsReview'
import { NeedsReviewTable } from '@/features/sku-alias/components/NeedsReviewTable'
import { NeedsReviewSelectionActionBar } from '@/features/sku-alias/components/NeedsReviewSelectionActionBar'
import { NeedsReviewSkeleton } from '@/features/sku-alias/components/NeedsReviewSkeleton'
import { ExternalUrlsTable } from '@/features/sku-alias/components/ExternalUrlsTable'
import {
  useStagingExternalUrlsQuery,
  useAssignUrlToVariantMutation,
  useUnassignUrlMutation,
  useAssignUrlToTagMutation,
  useUnassignTagMutation,
  useGeneralVariantQuery,
  useGenerateAiSuggestionsMutation,
  useClearSuggestionMutation,
  type StagingExternalUrl,
} from '@/features/sku-alias/api/stagingExternalUrls'
import { useTagsQuery } from '@/features/tags'
import {
  useMissingCostQuery,
  useUpdateVariantCostMutation,
  type UpdateVariantCostPayload,
} from '@/features/sku-alias/api/missingCost'
import { MissingCostTable } from '@/features/sku-alias/components/MissingCostTable'
import { ToggleGroup, ToggleGroupItem } from '@/components/ui/toggle-group'
import { Switch } from '@/components/ui/switch'
import { Label } from '@/components/ui/label'
import type {
  ListingId,
  ProductId,
  UnmatchedListing,
} from '@/features/sku-alias/schemas/unmatchedListingSchema'
import type { Variant } from '@/lib/variantService'
import { Button } from '@/components/ui/button'

const PAGE_SIZE = 20
const URL_PAGE_SIZE = 50
const COST_PAGE_SIZE = 20
const SOURCE_PARAM = 'source'

type MatchingSource = 'facebook-baselinker' | 'google-ads' | 'missing-cost'

const matchingSources: MatchingSource[] = ['facebook-baselinker', 'google-ads', 'missing-cost']

/** Props for the Needs Review tab content. */
export interface NeedsReviewTabProps {
  /** Whether the tab is currently active */
  isActive: boolean
}

/**
 * Temporary Needs Review tab content.
 *
 * @component
 */
export function NeedsReviewTab({
  isActive,
}: NeedsReviewTabProps): ReactElement {
  const { t } = useTranslation('features-sku-alias')
  const [searchParams, setSearchParams] = useSearchParams()

  // Source state management
  const urlSource = searchParams.get(SOURCE_PARAM)
  const activeSource: MatchingSource = matchingSources.includes(urlSource as MatchingSource)
    ? (urlSource as MatchingSource)
    : 'facebook-baselinker'

  const setActiveSource = useCallback(
    (next: MatchingSource): void => {
      if (next === activeSource) return
      const updated = new URLSearchParams(searchParams)
      if (next === 'facebook-baselinker') {
        updated.delete(SOURCE_PARAM)
      } else {
        updated.set(SOURCE_PARAM, next)
      }
      setSearchParams(updated, { replace: true })
    },
    [activeSource, searchParams, setSearchParams]
  )

  const handleSourceChange = useCallback(
    (value: string): void => {
      if (!value || value === activeSource) return
      if (matchingSources.includes(value as MatchingSource)) {
        setActiveSource(value as MatchingSource)
      }
    },
    [activeSource, setActiveSource]
  )

  const [page, setPage] = useState<number>(0)
  const [urlPage, setUrlPage] = useState<number>(0)
  const [sortKey, setSortKey] = useState<NeedsReviewSortKey>('totalUnattributed')
  const [direction, setDirection] = useState<'asc' | 'desc'>('desc')
  const [selectedIds, setSelectedIds] = useState<Set<ListingId>>(new Set<ListingId>())

  const query = useNeedsReviewQuery(
    {
      page,
      size: PAGE_SIZE,
      sort: sortKey,
      direction,
    },
    {
      enabled: isActive && activeSource === 'facebook-baselinker',
    }
  )

  const assignMutation = useAssignAliasMutation()
  const generateSkuAiSuggestionsMutation = useGenerateSkuAiSuggestionsMutation()
  const clearSkuSuggestionMutation = useClearSkuSuggestionMutation()

  // Toggle to show assigned (resolved) URLs
  const [showAssigned, setShowAssigned] = useState(false)

  // Staging External URLs queries (replaces Google Links)
  const externalUrlsQuery = useStagingExternalUrlsQuery(
    { resolved: showAssigned, page: urlPage, size: URL_PAGE_SIZE },
    { enabled: isActive && activeSource === 'google-ads' }
  )
  const assignUrlMutation = useAssignUrlToVariantMutation()
  const unassignUrlMutation = useUnassignUrlMutation()
  const assignUrlToTagMutation = useAssignUrlToTagMutation()
  const unassignTagMutation = useUnassignTagMutation()
  const generateAiSuggestionsMutation = useGenerateAiSuggestionsMutation()
  const clearSuggestionMutation = useClearSuggestionMutation()

  // Tags available for the unified inbox picker.
  const tagsQuery = useTagsQuery({ enabled: isActive && activeSource === 'google-ads' })
  const tags = useMemo(() => tagsQuery.data ?? [], [tagsQuery.data])
  
  // General variant for site-wide assignment
  const generalVariantQuery = useGeneralVariantQuery()
  const generalVariantId = generalVariantQuery.data?.id

  // Missing-cost review
  const [costPage, setCostPage] = useState<number>(0)
  const missingCostQuery = useMissingCostQuery(
    { page: costPage, size: COST_PAGE_SIZE },
    { enabled: isActive && activeSource === 'missing-cost' }
  )
  const updateCostMutation = useUpdateVariantCostMutation()
  const savingVariantId = updateCostMutation.isPending
    ? (updateCostMutation.variables?.id ?? null)
    : null

  useEffect(() => {
    if (!isActive) return
    setPage(0)
    setUrlPage(0)
    setCostPage(0)
  }, [isActive])

  // Reset URL page when toggling between assigned/unassigned
  useEffect(() => {
    setUrlPage(0)
  }, [showAssigned])



  const listings = useMemo(() => query.data?.content ?? [], [query.data?.content])

  const selectedCount = selectedIds.size

  const templateListing = useMemo(() => {
    if (selectedIds.size === 0) return undefined
    const firstSelectable = listings.find(
      listing => selectedIds.has(listing.id) && listing.canCreate
    )
    return firstSelectable ?? undefined
  }, [listings, selectedIds])

  const updateSelection = (updater: (previous: Set<ListingId>) => Set<ListingId>): void => {
    setSelectedIds(prev => {
      const result = updater(prev)
      return result
    })
  }

  const handleSortChange = (key: NeedsReviewSortKey): void => {
    setSortKey(prev => {
      if (prev === key) {
        setDirection(dir => (dir === 'asc' ? 'desc' : 'asc'))
        return prev
      }
      setDirection('desc')
      return key
    })
  }

  const handleToggleListing = (listing: UnmatchedListing): void => {
    if (!listing.canResolve) return
    updateSelection(prev => {
      const next = new Set(prev)
      if (next.has(listing.id)) {
        next.delete(listing.id)
      } else {
        next.add(listing.id)
      }
      return next
    })
  }

  const handleToggleAll = (pageListings: UnmatchedListing[], shouldSelect: boolean): void => {
    updateSelection(prev => {
      const next = new Set(prev)
      for (const listing of pageListings) {
        if (!listing.canResolve) {
          next.delete(listing.id)
          continue
        }
        if (shouldSelect) {
          next.add(listing.id)
        } else {
          next.delete(listing.id)
        }
      }
      return next
    })
  }



  const handleClearSelection = (): void => {
    setSelectedIds(new Set())
  }

  useEffect(() => {
    handleClearSelection()
  }, [activeSource])

  const assignListing = async (listing: UnmatchedListing, productId: number): Promise<void> => {
    await assignMutation.mutateAsync({
      slug: listing.externalSlug,
      productId: productId as ProductId,
    })
    updateSelection(prev => {
      const next = new Set(prev)
      next.delete(listing.id)
      return next
    })
    toast.success(t('needsReview.success.assigned'))
  }



  const [isBulkAssigning, setIsBulkAssigning] = useState(false)

  const handleBulkAssign = async (productId: number): Promise<void> => {
    setIsBulkAssigning(true)
    try {
      const listingMap = new Map(listings.map(listing => [listing.id, listing]))
      const selectedListings = Array.from(selectedIds)
        .map(listingId => listingMap.get(listingId))
        .filter((listing): listing is UnmatchedListing => Boolean(listing))
        .filter(listing => listing.canResolve)

      const promises = selectedListings.map(listing =>
        assignMutation.mutateAsync({ slug: listing.externalSlug, productId: productId as ProductId })
      )
      await Promise.all(promises)

      toast.success(t('needsReview.success.bulkAssigned', { count: selectedListings.length }))
      setSelectedIds(new Set())
    } catch (error) {
      console.error(error)
      toast.error(t('needsReview.errors.bulkAssignFailed'))
    } finally {
      setIsBulkAssigning(false)
    }
  }



  // External URL handlers (replaces Google Links handlers)
  const handleExternalUrlVariantSelect = useCallback(
    (url: StagingExternalUrl, variant: Variant): void => {
      assignUrlMutation.mutate(
        { uuid: url.uuid, variantId: variant.id },
        {
          onSuccess: result => {
            const reattributed = result.adSpendRecordsReattributed
            toast.success(t('needsReview.externalUrls.success.assignedToVariant'), {
              description:
                reattributed > 0
                  ? t('needsReview.externalUrls.success.assignedToVariantDescription', { count: reattributed })
                  : undefined,
            })
          },
          onError: err => {
            const msg = err instanceof Error ? err.message : t('needsReview.externalUrls.errors.assignFailedDescription')
            toast.error(t('needsReview.externalUrls.errors.assignFailed'), { description: msg })
          },
        }
      )
    },
    [assignUrlMutation, t]
  )

  const handleAssignToGeneral = useCallback(
    (url: StagingExternalUrl): void => {
      if (!generalVariantId) {
        toast.error(t('needsReview.externalUrls.errors.generalVariantUnavailable'))
        return
      }
      assignUrlMutation.mutate(
        { uuid: url.uuid, variantId: generalVariantId },
        {
          onSuccess: result => {
            const reattributed = result.adSpendRecordsReattributed
            toast.success(t('needsReview.externalUrls.success.assignedToSitewide'), {
              description:
                reattributed > 0
                  ? t('needsReview.externalUrls.success.assignedToSitewideDescription', { count: reattributed })
                  : undefined,
            })
          },
          onError: err => {
            const msg = err instanceof Error ? err.message : t('needsReview.externalUrls.errors.assignFailedDescription')
            toast.error(t('needsReview.externalUrls.errors.assignFailed'), { description: msg })
          },
        }
      )
    },
    [assignUrlMutation, generalVariantId, t]
  )

  const handleUnassignUrl = useCallback(
    (url: StagingExternalUrl): void => {
      unassignUrlMutation.mutate(
        { uuid: url.uuid },
        {
          onSuccess: result => {
            toast.success(t('needsReview.externalUrls.success.unassigned'), {
              description:
                result.adSpendRecordsReattributed > 0
                  ? t('needsReview.externalUrls.success.unassignedDescription', { count: result.adSpendRecordsReattributed })
                  : undefined,
            })
          },
          onError: err => {
            const msg = err instanceof Error ? err.message : t('needsReview.externalUrls.errors.unassignFailedDescription')
            toast.error(t('needsReview.externalUrls.errors.unassignFailed'), { description: msg })
          },
        }
      )
    },
    [unassignUrlMutation, t]
  )

  const handleTagSelect = useCallback(
    (url: StagingExternalUrl, tagUuid: string): void => {
      assignUrlToTagMutation.mutate(
        { uuid: url.uuid, tagUuid },
        {
          onSuccess: () => {
            toast.success(t('needsReview.externalUrls.success.assignedToTag'))
          },
          onError: err => {
            const msg = err instanceof Error ? err.message : t('needsReview.externalUrls.errors.assignTagFailedDescription')
            toast.error(t('needsReview.externalUrls.errors.assignTagFailed'), { description: msg })
          },
        }
      )
    },
    [assignUrlToTagMutation, t]
  )

  const handleUnassignTagUrl = useCallback(
    (url: StagingExternalUrl): void => {
      unassignTagMutation.mutate(
        { uuid: url.uuid },
        {
          onSuccess: () => {
            toast.success(t('needsReview.externalUrls.success.tagUnassigned'))
          },
          onError: err => {
            const msg = err instanceof Error ? err.message : t('needsReview.externalUrls.errors.unassignTagFailedDescription')
            toast.error(t('needsReview.externalUrls.errors.unassignTagFailed'), { description: msg })
          },
        }
      )
    },
    [unassignTagMutation, t]
  )

  const handleGenerateAiSuggestions = useCallback((): void => {
    generateAiSuggestionsMutation.mutate(undefined, {
      onSuccess: result => {
        toast.success(result.message)
      },
      onError: err => {
        const msg = err instanceof Error ? err.message : t('needsReview.externalUrls.errors.aiSuggestionFailedDescription')
        toast.error(t('needsReview.externalUrls.errors.aiSuggestionFailed'), { description: msg })
      },
    })
  }, [generateAiSuggestionsMutation, t])

  const handleClearSuggestion = useCallback(
    (url: StagingExternalUrl): void => {
      clearSuggestionMutation.mutate(
        { uuid: url.uuid },
        {
          onSuccess: () => {
            toast.success(t('needsReview.externalUrls.success.suggestionCleared'))
          },
          onError: err => {
            const msg = err instanceof Error ? err.message : t('needsReview.externalUrls.errors.clearSuggestionFailedDescription')
            toast.error(t('needsReview.externalUrls.errors.clearSuggestionFailed'), { description: msg })
          },
        }
      )
    },
    [clearSuggestionMutation, t]
  )

  // SKU AI suggestion handlers
  const handleGenerateSkuAiSuggestions = useCallback((): void => {
    generateSkuAiSuggestionsMutation.mutate(undefined, {
      onSuccess: result => {
        toast.success(result.message)
      },
      onError: err => {
        const msg = err instanceof Error ? err.message : t('needsReview.externalUrls.errors.aiSuggestionFailedDescription')
        toast.error(t('needsReview.externalUrls.errors.aiSuggestionFailed'), { description: msg })
      },
    })
  }, [generateSkuAiSuggestionsMutation, t])

  const handleClearSkuSuggestion = useCallback(
    (listing: UnmatchedListing): void => {
      clearSkuSuggestionMutation.mutate(
        { slug: listing.externalSlug },
        {
          onSuccess: () => {
            toast.success(t('needsReview.externalUrls.success.suggestionCleared'))
          },
          onError: err => {
            const msg = err instanceof Error ? err.message : t('needsReview.externalUrls.errors.clearSuggestionFailedDescription')
            toast.error(t('needsReview.externalUrls.errors.clearSuggestionFailed'), { description: msg })
          },
        }
      )
    },
    [clearSkuSuggestionMutation, t]
  )

  const paginationControls = useMemo(() => {
    return (
      <div className="flex items-center justify-between gap-2">
        <Button
          variant="ghost"
          disabled={page === 0 || query.isFetching}
          onClick={() => setPage(prev => Math.max(prev - 1, 0))}
        >
          {t('needsReview.pagination.previous')}
        </Button>
        <span className="text-xs uppercase tracking-wide text-muted-foreground">
          {t('needsReview.pagination.page', { page: page + 1 })}
        </span>
        <Button
          variant="ghost"
          disabled={query.data?.last === true || query.isFetching}
          onClick={() => setPage(prev => prev + 1)}
        >
          {t('needsReview.pagination.next')}
        </Button>
      </div>
    )
  }, [page, query.data?.last, query.isFetching, t])

  const renderFacebookBaselinkerContent = (): ReactElement => {
    if (query.isLoading) {
      return <NeedsReviewSkeleton />
    }

    if (query.isError) {
      return (
        <div className="flex flex-col items-center gap-4 rounded-3xl border border-border/15 bg-card px-10 py-20 text-center">
          <p className="text-sm text-destructive">
            {query.error instanceof Error
              ? query.error.message
              : t('needsReview.errors.loadFailed')}
          </p>
          <Button variant="outline" onClick={() => query.refetch()}>
            {t('needsReview.errors.tryAgain')}
          </Button>
        </div>
      )
    }

    const assigningListingId = assignMutation.isPending
      ? (listings.find(listing => listing.externalSlug === assignMutation.variables?.slug)?.id ?? null)
      : null

    return (
      <div className="space-y-4">
        <NeedsReviewTable
          listings={listings}
          sortKey={sortKey}
          direction={direction}
          selectedIds={selectedIds}
          onSortChange={handleSortChange}
          onToggleListing={handleToggleListing}
          onToggleAll={handleToggleAll}
          onAssignExisting={assignListing}
          assigningListingId={assigningListingId}
          isAssigning={assignMutation.isPending}
          onGenerateAiSuggestions={handleGenerateSkuAiSuggestions}
          isGeneratingAiSuggestions={generateSkuAiSuggestionsMutation.isPending}
          onClearSuggestion={handleClearSkuSuggestion}
        />
        {paginationControls}
      </div>
    )
  }

  const handleSaveCost = useCallback(
    (payload: UpdateVariantCostPayload): void => {
      updateCostMutation.mutate(payload, {
        onSuccess: () => {
          toast.success(t('needsReview.costReview.success.updated'))
        },
        onError: err => {
          const msg = err instanceof Error ? err.message : t('needsReview.costReview.errors.updateFailed')
          toast.error(t('needsReview.costReview.errors.updateFailed'), { description: msg })
        },
      })
    },
    [updateCostMutation, t]
  )

  const renderMissingCostContent = (): ReactElement => {
    if (missingCostQuery.isLoading) {
      return <NeedsReviewSkeleton />
    }

    if (missingCostQuery.isError) {
      return (
        <div className="flex flex-col items-center gap-4 rounded-3xl border border-border/15 bg-card px-10 py-20 text-center">
          <p className="text-sm text-destructive">
            {missingCostQuery.error instanceof Error
              ? missingCostQuery.error.message
              : t('needsReview.costReview.errors.loadFailed')}
          </p>
          <Button variant="outline" onClick={() => missingCostQuery.refetch()}>
            {t('needsReview.errors.tryAgain')}
          </Button>
        </div>
      )
    }

    const variants = missingCostQuery.data?.content ?? []

    if (variants.length === 0) {
      return (
        <div className="flex flex-col items-center gap-4 rounded-3xl border border-border/15 bg-card px-10 py-20 text-center">
          <p className="text-sm text-muted-foreground">{t('needsReview.costReview.empty')}</p>
        </div>
      )
    }

    const totalPages = missingCostQuery.data?.totalPages ?? 1
    const totalElements = missingCostQuery.data?.totalElements ?? 0
    const isLastPage = missingCostQuery.data?.last ?? true

    const costPaginationControls = (
      <div className="flex items-center justify-between gap-2">
        <Button
          variant="ghost"
          disabled={costPage === 0 || missingCostQuery.isFetching}
          onClick={() => setCostPage(prev => Math.max(prev - 1, 0))}
        >
          {t('needsReview.pagination.previous')}
        </Button>
        <span className="text-xs uppercase tracking-wide text-muted-foreground">
          {t('needsReview.pagination.page', { page: costPage + 1 })} of {totalPages} ({totalElements} total)
        </span>
        <Button
          variant="ghost"
          disabled={isLastPage || missingCostQuery.isFetching}
          onClick={() => setCostPage(prev => prev + 1)}
        >
          {t('needsReview.pagination.next')}
        </Button>
      </div>
    )

    return (
      <div className="space-y-4">
        <MissingCostTable variants={variants} onSave={handleSaveCost} savingId={savingVariantId} />
        {costPaginationControls}
      </div>
    )
  }

  const renderGoogleAdsContent = (): ReactElement => {
    if (externalUrlsQuery.isLoading) {
      return (
        <div className="p-8 text-center text-muted-foreground">
          {t('needsReview.externalUrls.loading')}
        </div>
      )
    }

    if (externalUrlsQuery.isError) {
      return (
        <div className="flex flex-col items-center gap-4 rounded-3xl border border-border/15 bg-card px-10 py-20 text-center">
          <p className="text-sm text-destructive">
            {externalUrlsQuery.error instanceof Error
              ? externalUrlsQuery.error.message
              : t('needsReview.externalUrls.errors.loadFailed')}
          </p>
          <Button variant="outline" onClick={() => externalUrlsQuery.refetch()}>
            {t('needsReview.errors.tryAgain')}
          </Button>
        </div>
      )
    }

    const urls = externalUrlsQuery.data?.content ?? []

    const toggleControl = (
      <div className="flex items-center gap-2">
        <Switch
          id="show-assigned"
          checked={showAssigned}
          onCheckedChange={setShowAssigned}
        />
        <Label htmlFor="show-assigned" className="text-sm text-muted-foreground cursor-pointer">
          {t('needsReview.externalUrls.showAssigned')}
        </Label>
      </div>
    )

    if (urls.length === 0) {
      return (
        <div className="space-y-4">
          {toggleControl}
          <div className="flex flex-col items-center gap-4 rounded-3xl border border-border/15 bg-card px-10 py-20 text-center">
            <p className="text-sm text-muted-foreground">
              {showAssigned ? t('needsReview.externalUrls.emptyAssigned') : t('needsReview.externalUrls.emptyUnassigned')}
            </p>
          </div>
        </div>
      )
    }

    const totalElements = externalUrlsQuery.data?.totalElements ?? 0
    const totalPages = externalUrlsQuery.data?.totalPages ?? 1
    const isLastPage = externalUrlsQuery.data?.last ?? true

    const urlPaginationControls = (
      <div className="flex items-center justify-between gap-2">
        <Button
          variant="ghost"
          disabled={urlPage === 0 || externalUrlsQuery.isFetching}
          onClick={() => setUrlPage(prev => Math.max(prev - 1, 0))}
        >
          {t('needsReview.pagination.previous')}
        </Button>
        <span className="text-xs uppercase tracking-wide text-muted-foreground">
          {t('needsReview.pagination.page', { page: urlPage + 1 })} of {totalPages} ({totalElements} total)
        </span>
        <Button
          variant="ghost"
          disabled={isLastPage || externalUrlsQuery.isFetching}
          onClick={() => setUrlPage(prev => prev + 1)}
        >
          {t('needsReview.pagination.next')}
        </Button>
      </div>
    )

    return (
      <div className="space-y-4">
        {toggleControl}
        <div className="rounded-3xl border border-border/15 bg-card pb-6">
          <ExternalUrlsTable
            urls={urls}
            tags={tags}
            onVariantSelect={handleExternalUrlVariantSelect}
            onTagSelect={handleTagSelect}
            onAssignToGeneral={showAssigned ? undefined : handleAssignToGeneral}
            onUnassign={showAssigned ? handleUnassignUrl : undefined}
            onUnassignTag={showAssigned ? handleUnassignTagUrl : undefined}
            onClearSuggestion={showAssigned ? undefined : handleClearSuggestion}
            generalVariantId={generalVariantId}
            isLoading={
              assignUrlMutation.isPending ||
              unassignUrlMutation.isPending ||
              assignUrlToTagMutation.isPending ||
              unassignTagMutation.isPending ||
              clearSuggestionMutation.isPending
            }
            showAssigned={showAssigned}
            onGenerateAiSuggestions={handleGenerateAiSuggestions}
            isGeneratingAiSuggestions={generateAiSuggestionsMutation.isPending}
          />
        </div>
        {urlPaginationControls}
      </div>
    )
  }

  return (
    <div className="mx-auto max-w-screen-2xl space-y-6 px-0 pb-16 pt-6 lg:px-8">
      <div className="space-y-4">
        <ToggleGroup
          type="single"
          value={activeSource}
          onValueChange={handleSourceChange}
          className="bg-muted/50 border border-border/60 justify-start"
        >
          <ToggleGroupItem
            value="facebook-baselinker"
            aria-label={t('needsReview.tabs.facebookBaselinker')}
            className="px-5 py-2 text-sm font-medium text-muted-foreground data-[state=on]:bg-muted-selector data-[state=on]:text-foreground flex-1"
          >
            {t('needsReview.tabs.facebookBaselinker')}
          </ToggleGroupItem>
          <ToggleGroupItem
            value="google-ads"
            aria-label={t('needsReview.tabs.googleAds')}
            className="px-5 py-2 text-sm font-medium text-muted-foreground data-[state=on]:bg-muted-selector data-[state=on]:text-foreground flex-1"
          >
            {t('needsReview.tabs.googleAds')}
          </ToggleGroupItem>
          <ToggleGroupItem
            value="missing-cost"
            aria-label={t('needsReview.tabs.cost')}
            className="px-5 py-2 text-sm font-medium text-muted-foreground data-[state=on]:bg-muted-selector data-[state=on]:text-foreground flex-1"
          >
            {t('needsReview.tabs.cost')}
          </ToggleGroupItem>
        </ToggleGroup>

      </div>

      <AnimatePresence mode="wait" initial={false}>
        <motion.div
          key={activeSource}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.2, ease: 'easeOut' }}
        >
          {activeSource === 'facebook-baselinker'
            ? renderFacebookBaselinkerContent()
            : activeSource === 'google-ads'
              ? renderGoogleAdsContent()
              : renderMissingCostContent()}
        </motion.div>
      </AnimatePresence>
      <NeedsReviewSelectionActionBar
        count={activeSource === 'facebook-baselinker' ? selectedCount : 0}
        isBusy={isBulkAssigning}
        onAssignBulk={handleBulkAssign}
        templateListing={activeSource === 'facebook-baselinker' ? templateListing : undefined}
        onClearSelection={handleClearSelection}
      />
    </div>
  )
}

export default NeedsReviewTab
